import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Lock, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const LOGO_URL =
  "/vznlogo.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const isSetup = params.get("setup") === "1";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { setErr("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. You can now sign in.");
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Link to="/" className="inline-flex w-full max-w-[14rem]">
            <img src={LOGO_URL} alt="Vision Grafix" className="w-full h-auto object-contain" />
          </Link>
        </div>

        {!token ? (
          <div className="border border-[#FF2D2D] bg-[#FF2D2D]/5 p-6" data-testid="reset-no-token">
            <h2 className="font-display text-3xl mb-2 uppercase">Bad link</h2>
            <p className="text-sm text-[#a3a3a3] mb-6">This reset link is missing its token. Request a fresh one.</p>
            <Link to="/forgot-password" className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider">
              Request new link
            </Link>
          </div>
        ) : done ? (
          <div className="border border-[#262626] bg-[#0a0a0a] p-6" data-testid="reset-success">
            <CheckCircle size={32} weight="fill" className="text-[#FF2D2D] mb-4" />
            <h2 className="font-display text-3xl mb-2 uppercase">Password updated.</h2>
            <p className="text-sm text-[#a3a3a3] mb-6">Redirecting you to sign in…</p>
            <Link to="/login" className="btn-primary px-5 py-3 text-sm font-bold uppercase tracking-wider">
              Sign in now →
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="reset-form">
            <h2 className="font-display text-4xl mb-2 uppercase">{isSetup ? "Set your password" : "Set new password"}</h2>
            <p className="text-sm text-[#a3a3a3] mb-8">{isSetup ? "Welcome aboard. Pick a password to activate your client portal." : "Pick a strong one. You'll use it next time you sign in."}</p>

            {err && (
              <div className="mb-4 p-3 border border-[#ff2a00] bg-[#ff2a00]/10 text-sm text-[#ff8866]" data-testid="reset-error">
                {err}
              </div>
            )}

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2 flex items-center gap-2">
              <Lock size={12} /> New password
            </label>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="reset-password"
              className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 text-white focus:border-[#FF2D2D] outline-none"
            />
            <div className="text-xs text-[#666] mt-1">Min 6 characters.</div>

            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mt-5 mb-2">Confirm password</label>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm"
              className="w-full bg-[#0a0a0a] border border-[#262626] px-4 py-3 text-white focus:border-[#FF2D2D] outline-none"
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="btn-primary w-full mt-6 px-6 py-3 text-sm font-bold uppercase tracking-wider"
            >
              {loading ? "Updating…" : "Update password →"}
            </button>

            <Link to="/login" className="mt-6 text-sm text-[#a3a3a3] hover:text-white inline-flex items-center gap-2" data-testid="reset-back">
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
